<script setup lang="ts">
import { helpers } from '@vuelidate/validators'

import { CustomerTypeEnum, GenderEnum, Identification, Person } from '~/layers/forms/models'
import { emailValidator, requiredValidator } from '~/utils/validators'

definePageMeta({
  layout: 'focus',
  middleware: 'unauthenticated',
})

const { cloned: credentials, sync: resetAuth } = useCloned<
  Nullable<{
    email: string
    confirmEmail: string
    password: string
    confirmPassword: string
    fullName: string
    firstLastName: string
    secondName: string
    secondLastName: string
    birthDate: Date
    gender: PropsTypesOf<typeof GenderEnum>['code']
    mobile: string
    registrationSucceded: boolean
    requiredEmailValidation: boolean
    userId: string
  }> & { identification: Identification }
>({
  email: null,
  confirmEmail: null,
  password: null,
  confirmPassword: null,
  fullName: null,
  firstLastName: null,
  secondName: null,
  secondLastName: null,
  birthDate: null,
  gender: null,
  mobile: null,
  registrationSucceded: false,
  requiredEmailValidation: true,
  userId: null,
  identification: Identification.load(),
})

const toast = useToast()
const { $siteApi } = useNuxtApp()

const v$ = useValidation(
  () => ({
    email: { required: requiredValidator, email: emailValidator },
    confirmEmail: {
      required: requiredValidator,
      sameAs: helpers.withMessage(
        'El correo no coincide.',
        () => credentials.value.confirmEmail === credentials.value.email
      ),
    },
    password: { required: requiredValidator },
    confirmPassword: {
      required: requiredValidator,
      sameAs: helpers.withMessage(
        'La contrseña no coincide.',
        () => credentials.value.confirmPassword === credentials.value.password
      ),
    },
  }),
  credentials,
  {
    $scope: 'auth',
    $registerAs: 'register',
  }
)

const isLoading = ref(false)

const register = async () => {
  if (!(await v$.value.$validate())) return

  isLoading.value = true

  try {
    const body: SiteApiSchema<'RegisterRequestModel'> = {
      email: credentials.value.email,
      password: credentials.value.password,
    }

    const { birthDate, firstLastName, fullName, gender, mobile, identification, secondLastName } =
      credentials.value

    if (
      birthDate ||
      firstLastName ||
      fullName ||
      gender ||
      mobile ||
      identification.number ||
      secondLastName
    ) {
      body.additionalData = {
        birthDate: credentials.value.birthDate ? credentials.value.birthDate.toJSON() : undefined,
        firstLastName: credentials.value.firstLastName,
        firstName: credentials.value.fullName,
        gender: credentials.value.gender ?? GenderEnum.Unknown.code,
        mobile: credentials.value.mobile,
        nationalId: credentials.value.identification.number,
        secondLastName: credentials.value.secondLastName,
      }
    }

    const result: SiteApiSchema<'UserResponseModel'> = await $siteApi('/register', {
      method: 'post',
      body,
    })

    credentials.value.userId = result?.id ?? null
    credentials.value.registrationSucceded = true
  } catch {
    toast.add({
      severity: 'error',
      summary: 'Perfil',
      detail: 'Hubo un problema al intentar crear tu cuenta.',
      life: 5000,
    })
  }
  isLoading.value = false
}

watch(
  () => credentials.value.requiredEmailValidation,
  (n, o) => {
    if (!n && o) window.location.replace('/user/login')
  }
)
</script>

<template>
  <div class="flex flex-col items-center justify-center w-full lg:justify-center">
    <CardComponent
      class="w-full md:max-w-2xl"
      bg="light"
    >
      <template #header>
        <div class="py-4">
          <ImageComponent
            src="/img/logo-contigo.png"
            alt="Contigo"
            class="h-14"
          />
        </div>
      </template>

      <div class="flex flex-col gap-4 p-4">
        <ProcessingComponent
          v-if="isLoading"
          loading-message="Por favor espere..."
        />

        <template v-else-if="credentials.registrationSucceded">
          <AuthEmailConfirmationForm
            v-model="credentials.requiredEmailValidation"
            :user-id="credentials.userId"
            :email="credentials.email"
          >
            <template #aditional-actions>
              <PvButton
                text
                style-as-input
                @click="resetAuth()"
              >
                Intentar con otro correo
              </PvButton>
            </template>
          </AuthEmailConfirmationForm>
        </template>
        <template v-else>
          <GridComponent
            class="my-4"
            :cols="2"
          >
            <InputField
              label="Correo Electrónico"
              :error="v$.email?.$error"
              :error-message="v$.email?.$errors[0]?.$message.toString()"
            >
              <InputAddon :prefix="{ IconClass: 'icon-[ph--at-bold]' }">
                <PvInputText v-model.lowercase="credentials.email" />
              </InputAddon>
            </InputField>
            <InputField
              label="Confirmar Correo Electrónico"
              :error="v$.confirmEmail?.$error"
              :error-message="v$.confirmEmail?.$errors[0]?.$message.toString()"
            >
              <InputAddon :prefix="{ IconClass: 'icon-[ph--at-bold]' }">
                <PvInputText v-model.lowercase="credentials.confirmEmail" />
              </InputAddon>
            </InputField>
            <InputField
              label="Contraseña"
              :error="v$.password?.$error"
              :error-message="v$.password?.$errors[0]?.$message.toString()"
            >
              <PvPassword
                v-model="credentials.password"
                fluid
                toggle-mask
              />
            </InputField>
            <InputField
              label="Confirmar Contraseña"
              :error="v$.confirmPassword?.$error"
              :error-message="v$.confirmPassword?.$errors[0]?.$message.toString()"
            >
              <PvPassword
                v-model="credentials.confirmPassword"
                :feedback="false"
                fluid
                toggle-mask
              />
            </InputField>
            <PvAccordion class="col-span-full">
              <PvAccordionPanel value="0">
                <PvAccordionHeader>Otros Datos</PvAccordionHeader>
                <PvAccordionContent>
                  <GridComponent
                    class="mt-4"
                    :cols="2"
                  >
                    <FormsIdentificationFields
                      v-model="credentials.identification"
                      validation-scope="register"
                      ignore-validations
                      :entity-type="CustomerTypeEnum.Person.code"
                    />

                    <InputField
                      label="Nombre"
                      :error="v$.fullName?.$error"
                      :error-message="v$.fullName?.$errors[0]?.$message.toString()"
                    >
                      <PvInputText v-model="credentials.fullName" />
                    </InputField>

                    <InputField
                      label="Primer Apellido"
                      :error="v$.firstLastName?.$error"
                      :error-message="v$.firstLastName?.$errors[0]?.$message.toString()"
                    >
                      <PvInputText v-model="credentials.firstLastName" />
                    </InputField>
                    <InputField
                      label="Fecha de nacimiento"
                      :error="v$.birthDate?.$error"
                      :error-message="v$.birthDate?.$errors[0]?.$message.toString()"
                    >
                      <PvDatePicker
                        v-model="credentials.birthDate"
                        :max-date="new Date()"
                      />
                    </InputField>

                    <InputField label="Segundo Apellido">
                      <PvInputText v-model="credentials.secondLastName" />
                    </InputField>

                    <InputField label="Género">
                      <PvSelect
                        v-model="credentials.gender"
                        option-label="label"
                        option-value="code"
                        :options="Person.getGenderTypeList()"
                      />
                    </InputField>

                    <InputField label="Número móvil (Whatsapp)">
                      <InputAddon :prefix="{ IconClass: 'icon-[ph--phone-bold] ' }">
                        <PvInputMask
                          v-model="credentials.mobile"
                          mask="(999) 999-9999"
                        />
                      </InputAddon>
                    </InputField>
                  </GridComponent>
                </PvAccordionContent>
              </PvAccordionPanel>
            </PvAccordion>
          </GridComponent>
          <PvButton
            class="text-lg font-bold"
            style-as-input
            @click="register()"
          >
            Registrar
          </PvButton>
          <div class="flex">
            <PvDivider />
            <span class="text-nowrap px-2">Ya tienes cuenta?</span>
            <PvDivider />
          </div>
          <PvButton
            severity="contrast"
            as="a"
            style-as-input
            href="/user/login"
            class="text-lg"
          >
            Iniciar sesión
          </PvButton>
        </template>
      </div>
    </CardComponent>
  </div>
</template>
